/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * 로컬 데이터 프로바이더 클래스를 정의한다.
 *
 * @file /src/models/store/provider/Local.ts
 * @author Arzz <arzz@arzz.com>
 * @license MIT License
 * @modified 2026. 6. 14.
 */
import * as parent from '@models/store/Provider';
import { Stores } from '@models/store/Store';

export interface Properties extends parent.Properties {
    /**
     * 레코드데이터
     */
    datas?: any[][];

    /**
     * 레코드 데이터 필드
     */
    dataFields?: string[];
}

export class Local extends parent.Provider {
    /**
     * 레코드데이터
     */
    datas: any[][] = null;

    /**
     * 레코드 데이터 필드
     */
    dataFields?: string[] = null;

    /**
     * 로컬 데이터 프로바이더 클래스를 정의한다.
     *
     * @param store - 데이터스토어 인스턴스
     * @param properties - 초기속성
     */
    constructor(
        public store: Stores,
        public properties: Properties = null
    ) {
        super(store, properties);

        this.datas = this.properties.datas ?? [];
        this.dataFields = this.properties.dataFields ?? [];
    }

    /**
     * 데이터를 불러오기 위한 매개변수를 가져온다.
     *
     * @returns 매개변수
     */
    async getLoaderParams(): Promise<{ [key: string]: any }> {
        return null;
    }

    /**
     * 데이터를 불러온다.
     */
    async load(): Promise<void> {
        if (this.loaded == true) {
            return;
        }

        return this.#load();
    }

    /**
     * 레코드 데이터를 가져온다.
     *
     * @returns 레코드 데이터
     */
    getDatas(): any[][] {
        return [...this.datas];
    }

    /**
     * 실제 데이터를 불러온다.
     */
    async #load(): Promise<void> {
        const records: { [key: string]: any }[] = [];
        if (this.loader !== null) {
            await this.store.onBeforeLoad();
            records.push(...(await this.loader()));
        } else {
            await this.store.onBeforeLoad();
            let datas = this.getDatas();
            datas.forEach((item) => {
                const record: { [key: string]: any } = {};
                this.dataFields.forEach((field, index) => {
                    record[field] = item[index];
                });
                records.push(record);
            });
        }

        await this.store.init(records);

        this.loaded = true;
    }

    /**
     * 데이터 로더를 초기화한다.
     */
    async reset(): Promise<void> {
        this.loaded = false;
    }

    /**
     * 변경된 데이터를 커밋한다.
     *
     * @param is_all - 전체 데이터를 커밋할지 여부 (false 인 경우 변경된 데이터만 커밋)
     * @returns 변경 완료 여부
     */
    async commit(is_all: boolean = false): Promise<boolean> {
        for (const record of this.store.getRecords().values()) {
            if (record.isUpdated() == true) {
                record.commit();
            }
        }

        return true;
    }
}
